import { Controller } from "@hotwired/stimulus"

// Stimulus controller for Strava API V2 (Leaderboard) sync
export default class extends Controller {
  static values = {
    routeId: String,
    challengeId: String,
    challengeName: String
  }

  async syncLeaderboard(event) {
    const button = event.currentTarget
    const originalText = button.innerHTML

    // Disable button and show loading state
    button.disabled = true
    button.innerHTML = '<i class="zmdi zmdi-refresh zmdi-hc-spin"></i> Sincronizando...'

    // Hide "Ver Resultados" button for this challenge during sync
    this.hideResultsButtonForChallenge(this.challengeIdValue)

    try {
      // Capturar o last_synced_at ANTES de iniciar o sync
      const currentPath = window.location.pathname
      const hasLocale = currentPath.match(/^\/(pt-BR|en)\//)
      const localePrefix = hasLocale ? hasLocale[0] : '/'
      const statusUrl = `${localePrefix}events/${this.getEventId()}/routes/${this.routeIdValue}/strava_sync/status`.replace('//', '/')
      const statusResponse = await fetch(statusUrl)
      const statusData = await statusResponse.json()
      const currentChallenge = statusData.challenges.find(c => c.strava_id === this.challengeIdValue)
      this.lastSyncedAtBeforeSync = currentChallenge?.last_synced_at || null

      const url = `${localePrefix}events/${this.getEventId()}/routes/${this.routeIdValue}/strava_sync/v2`.replace('//', '/')

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          challenge_id: this.challengeIdValue,
          challenge_name: this.challengeNameValue
        })
      })

      if (!response.ok) {
        this.showMessage('error', `Erro HTTP ${response.status}: Falha na sincronização`)
        button.disabled = false
        button.innerHTML = originalText
        this.showResultsButtonForChallenge(this.challengeIdValue)
        return
      }

      const data = await response.json()

      if (data.success) {
        this.showMessage('success', data.message)
        this.startStatusPolling(button, originalText)
      } else {
        const errorMessage = data.error || data.message || 'Erro ao sincronizar'
        this.showMessage('error', errorMessage)
        button.disabled = false
        button.innerHTML = originalText
        this.showResultsButtonForChallenge(this.challengeIdValue)
      }
    } catch (error) {
      this.showMessage('error', `Erro ao conectar com o servidor: ${error.message}`)
      button.disabled = false
      button.innerHTML = originalText
      this.showResultsButtonForChallenge(this.challengeIdValue)
    }
  }

  startStatusPolling(button, originalText) {
    this.pollingInterval = setInterval(async () => {
      try {
        const currentPath = window.location.pathname
        const hasLocale = currentPath.match(/^\/(pt-BR|en)\//)
        const localePrefix = hasLocale ? hasLocale[0] : '/'
        const statusUrl = `${localePrefix}events/${this.getEventId()}/routes/${this.routeIdValue}/strava_sync/status`.replace('//', '/')
        const response = await fetch(statusUrl)
        const data = await response.json()

        // Check if this specific challenge finished syncing
        const challenge = data.challenges.find(c => c.strava_id === this.challengeIdValue)

        // Verifica se o last_synced_at foi ATUALIZADO (diferente do valor antes do sync)
        if (challenge && challenge.last_synced_at && challenge.last_synced_at !== this.lastSyncedAtBeforeSync) {
          this.stopStatusPolling()
          this.showSyncComplete(button, originalText)
        }
      } catch (error) {
        this.stopStatusPolling()
        button.disabled = false
        button.innerHTML = originalText
        this.showResultsButtonForChallenge(this.challengeIdValue)
      }
    }, 3000) // Poll every 3 seconds
  }

  stopStatusPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  showSyncComplete(button, originalText) {
    button.disabled = false
    button.innerHTML = originalText

    // Refresh the page immediately to show updated sync status and new buttons
    window.location.reload()
  }

  showMessage(type, message) {
    // Remove existing alerts to avoid stacking
    document.querySelectorAll('.alert-global').forEach(alert => alert.remove())

    // Create alert using the same pattern as the layout
    const alertClass = type === 'success' ? 'alert-success' : 'alert-danger'
    const iconClass = type === 'success' ? 'zmdi-check-circle' : 'zmdi-alert-circle'
    const duration = type === 'success' ? 6000 : 8000

    const alertHTML = `
      <div class="alert ${alertClass} alert-dismissible fade show alert-global" role="alert" data-bs-auto-dismiss="${duration}">
        <i class="zmdi ${iconClass} alert-icon" aria-hidden="true"></i>
        ${message}
        <div class="alert-progress"></div>
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>
    `

    // Find the content area and prepend the alert
    const contentArea = document.querySelector('.content') || document.querySelector('main') || document.body
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = alertHTML
    const alertElement = tempDiv.firstElementChild

    contentArea.insertBefore(alertElement, contentArea.firstChild)

    // Apply the auto-dismiss functionality (same as vendors.js)
    alertElement.style.setProperty('--alert-duration', `${duration}ms`)

    setTimeout(() => {
      if (alertElement.parentNode) {
        // eslint-disable-next-line no-undef
        const bsAlert = new bootstrap.Alert(alertElement)
        bsAlert.close()
      }
    }, duration)
  }

  getEventId() {
    const pathParts = window.location.pathname.split('/')
    const eventIndex = pathParts.indexOf('events')
    return pathParts[eventIndex + 1]
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  hideResultsButtonForChallenge(challengeId) {
    const resultsButtons = document.querySelectorAll(`[data-challenge-id="${challengeId}"]`)

    resultsButtons.forEach(button => {
      if (button.href && button.href.includes('results')) {
        button.style.setProperty('display', 'none', 'important')
        button.style.setProperty('visibility', 'hidden', 'important')
        button.style.setProperty('opacity', '0', 'important')
        button.setAttribute('data-sync-hidden', 'true')
      }
    })
  }

  showResultsButtonForChallenge(challengeId) {
    const resultsButtons = document.querySelectorAll(`[data-challenge-id="${challengeId}"][data-sync-hidden="true"]`)
    resultsButtons.forEach(button => {
      if (button.href && button.href.includes('results')) {
        button.style.removeProperty('display')
        button.style.removeProperty('visibility')
        button.style.removeProperty('opacity')
        button.removeAttribute('data-sync-hidden')
      }
    })
  }

  disconnect() {
    this.stopStatusPolling()
  }
}
